import React from 'react';
import { Helmet } from 'react-helmet-async';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { matchPathname } from '../util/routes';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';
// License information for v3.7.0 of the mapbox-gl-js library:
// https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt
const MAPBOX_GL_JS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js';
const MAPBOX_GL_CSS_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css';

// The route of the full page load. SSR passes the request URL (with the query string) and the
// client passes location.pathname: both are matched by the path only, so they agree.
const initialRouteConfig = (initialPathname, routeConfiguration) => {
  const pathname = initialPathname.split(/[?#]/)[0];
  const matchedRoutes = matchPathname(pathname, routeConfiguration);
  return matchedRoutes.length > 0 ? matchedRoutes[0]?.route : null;
};

/**
 * Map library is shown on some of the pages, but ReusableMapContainer is used app wide.
 * The full page load of a route that shows a map (`includeMapLibrary` or
 * `prioritizeMapLibraryLoading` in the route configuration) includes the Mapbox GL library in
 * <head>, deferred unless the route prioritizes it. Other pages leave it out (backlog #2, P1):
 * a map reached later by in-app navigation, or a place picked in a location search, loads it with
 * loadMapboxLibrary.
 * Note: this currently only affects Mapbox library.
 * Google Maps library is always loaded immediately. (It seems to be more fragile when loaded asynchronously.)
 *
 * @param {string} initialPathname - The initial pathname at the time of the full page load.
 * @param {array} routeConfiguration - The route configuration.
 * @returns {Object} - { include, defer }: whether <head> gets the library and whether it is deferred.
 */
export const mapLibraryLoadingForPath = (initialPathname, routeConfiguration) => {
  if (!initialPathname) {
    return { include: true, defer: false };
  }
  const currentRouteConfig = initialRouteConfig(initialPathname, routeConfiguration);
  const prioritize = currentRouteConfig?.prioritizeMapLibraryLoading === true;
  const include = prioritize || currentRouteConfig?.includeMapLibrary === true;
  return { include, defer: !prioritize };
};

// The Mapbox access token from the config, registered by IncludeScripts: the loader gives it to the
// library, and the geocoder uses it before the library is on the page.
let registeredMapboxAccessToken = null;
export const getMapboxAccessToken = () =>
  (typeof window !== 'undefined' && window.mapboxgl?.accessToken) || registeredMapboxAccessToken;
let mapboxLibraryPromise = null;
// The stylesheet settles once per page; a retry after a failed script does not wait for it again.
let mapboxCssPromise = null;

// Resolves when the element has loaded (or already has); the error rejects.
const elementLoaded = (element, isLoaded) =>
  new Promise((resolve, reject) => {
    if (isLoaded()) {
      resolve();
      return;
    }
    const onError = () => reject(new Error(`Failed to load ${element.src || element.href}`));
    element.addEventListener('load', () => resolve(), { once: true });
    element.addEventListener('error', onError, { once: true });
  });

/**
 * Load the Mapbox GL library (JS + CSS) on demand and resolve when the map can be created
 * (backlog #2, P1). Uses the <head> tags of a full page load when they exist (deferred script)
 * and adds them otherwise, e.g. after in-app navigation from a page without a map. One load per
 * page; a failed load can be retried by the next call.
 *
 * @returns {Promise} resolves when window.mapboxgl and its stylesheet are ready.
 */
export const loadMapboxLibrary = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Mapbox library can only be loaded in the browser'));
  }
  if (mapboxLibraryPromise) {
    return mapboxLibraryPromise;
  }

  const head = document.head;
  if (!mapboxCssPromise) {
    let cssLink = head.querySelector(`link[rel="stylesheet"][href="${MAPBOX_GL_CSS_URL}"]`);
    if (!cssLink) {
      cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = MAPBOX_GL_CSS_URL;
      cssLink.crossOrigin = 'anonymous';
      // Before the app styles, where the server-rendered <head> has it: same cascade order.
      head.insertBefore(cssLink, head.querySelector('link[rel="stylesheet"], style'));
    }
    // The map works without its stylesheet (as with a failed <head> link): only wait for it.
    mapboxCssPromise = elementLoaded(cssLink, () => !!cssLink.sheet).catch(() => {});
  }

  // A <head> script that is still loading is waited for. (If it had already failed before this
  // call, its error is not seen again and the map keeps its placeholder, as without the loader.)
  let script = document.querySelector(`script[src="${MAPBOX_GL_JS_URL}"]:not([data-load-failed])`);
  const isInjected = !script && !window.mapboxgl;
  if (isInjected) {
    script = document.createElement('script');
    script.id = MAPBOX_SCRIPT_ID;
    script.src = MAPBOX_GL_JS_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    head.appendChild(script);
  }
  const jsReady = window.mapboxgl
    ? Promise.resolve()
    : elementLoaded(script, () => !!window.mapboxgl);

  mapboxLibraryPromise = Promise.all([jsReady, mapboxCssPromise])
    .then(() => {
      if (!window.mapboxgl) {
        throw new Error('Mapbox library did not initialize');
      }
      if (!window.mapboxgl.accessToken && registeredMapboxAccessToken) {
        window.mapboxgl.accessToken = registeredMapboxAccessToken;
      }
    })
    .catch(e => {
      // Let the next map or location search try again with a fresh script.
      mapboxLibraryPromise = null;
      if (script) {
        script.setAttribute('data-load-failed', '');
        if (isInjected && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      }
      throw e;
    });
  return mapboxLibraryPromise;
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;

  const routeConfiguration = useRouteConfiguration();
  // Note: Affects Mapbox only. Google Maps initialization is not yet ready to support asynchronous loading.
  const mapLibraryLoading = mapLibraryLoadingForPath(props?.initialPathname, routeConfiguration);
  const deferMapLibrary = mapLibraryLoading.defer ? { defer: '' } : {};

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk@0.16.2/mapbox-sdk.min.js`}
        async
      ></script>
    );
    if (mapLibraryLoading.include) {
      // Add CSS for Mapbox map
      mapLibraries.push(
        <link
          key="mapbox_GL_CSS"
          href={MAPBOX_GL_CSS_URL}
          rel="stylesheet"
          crossOrigin="anonymous"
        />
      );
      // Add Mapbox library
      mapLibraries.push(
        <script
          id={MAPBOX_SCRIPT_ID}
          key="mapbox_GL_JS"
          src={MAPBOX_GL_JS_URL}
          crossOrigin="anonymous"
          {...deferMapLibrary}
        ></script>
      );
    }
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin="anonymous"
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin="anonymous"
      ></script>
    );

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Ensure that gtag function is found from window scope
      window.gtag = function gtag() {
        dataLayer.push(arguments);
      };
      gtag('js', new Date());
      gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
      });
    }
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin="anonymous"
      ></script>
    );
  }

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;
  if (isBrowser && isMapboxInUse) {
    registeredMapboxAccessToken = mapboxAccessToken;
  }

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      // Add access token for Mapbox sdk.
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
